<template>
  <div class="config-container">
    <div v-if="loading" class="loading-overlay">
      <div class="loading-spinner">
        <div class="spinner"></div>
        <p>{{ t('koreaConfig.loading') }}</p>
      </div>
    </div>

    <div class="page-head">
      <div>
        <h2 class="page-title">{{ isEditMode ? t('koreaConfig.editTitle') : t('koreaConfig.createTitle') }}</h2>
        <p class="page-subtitle">{{ t('koreaConfig.subtitle') }}</p>
      </div>
    </div>

    <div class="config-card">
      <div class="config-form">
        <div class="form-item">
          <label class="form-label">{{ t('koreaConfig.configName') }}</label>
          <input
            v-model="configForm.name"
            class="native-input"
            type="text"
            :placeholder="t('koreaConfig.configNamePlaceholder')"
            :disabled="isEditMode && isDefaultConfig"
            :title="isEditMode && isDefaultConfig ? t('koreaConfig.defaultNameLocked') : ''"
          />
        </div>

        <div class="form-item">
          <label class="form-label">{{ t('koreaConfig.configDesc') }}</label>
          <input
            v-model="configForm.description"
            class="native-input"
            type="text"
            :placeholder="t('koreaConfig.configDescPlaceholder')"
            :disabled="isViewMode || (isEditMode && isDefaultConfig)"
          />
        </div>

        <div class="section-title">{{ t('koreaConfig.hangSection') }}</div>
        <div class="form-item form-item--one-line form-item--hang-map">
          <label class="form-label form-label--inline" for="hang-map-select-kr">{{ t('koreaConfig.hangMap') }}</label>
          <div class="hang-map-field-body">
            <el-select
              id="hang-map-select-kr"
              v-model="selectedHangMap"
              class="hang-map-el-select"
              filterable
              clearable
              :filter-method="setHangMapFilterQuery"
              :placeholder="t('koreaConfig.hangMapPlaceholder')"
              :disabled="isViewMode || (isEditMode && isDefaultConfig)"
              popper-class="hang-map-select-popper"
              fit-input-width
              :no-match-text="t('koreaConfig.hangMapNoMatch')"
              :no-data-text="t('koreaConfig.hangMapNoData')"
              teleported
              @visible-change="onHangMapDropdownVisible"
            >
              <el-option
                v-for="row in filteredHangMapRowsForSelect"
                :key="row.id != null ? row.id : row.optionValue"
                :label="row.optionValue"
                :value="row.optionValue"
              />
              <template #empty>
                <div class="hang-map-dropdown-empty">
                  {{ t('koreaConfig.hangMapEmpty') }}
                </div>
              </template>
            </el-select>
          </div>
        </div>

        <template v-if="isTreasureIslandMap">
          <div class="section-title">{{ t('koreaConfig.mapSpecificSection') }}</div>
          <p class="section-hint">{{ t('koreaConfig.treasureIslandFightHint') }}</p>
          <div
            v-for="layer in treasureIslandLayers"
            :key="layer.id"
            class="treasure-layer-row"
          >
            <span class="treasure-layer-label">{{ layer.label }}</span>
            <div class="treasure-layer-range">
              <input
                v-model.number="treasureIslandFight[layer.minKey]"
                class="native-input treasure-sec-input"
                type="number"
                :min="FIGHT_SEC_MIN"
                :max="FIGHT_SEC_MAX"
                step="1"
                :disabled="fieldsDisabled"
              />
              <span class="auto-key-range-sep">～</span>
              <input
                v-model.number="treasureIslandFight[layer.maxKey]"
                class="native-input treasure-sec-input"
                type="number"
                :min="FIGHT_SEC_MIN"
                :max="FIGHT_SEC_MAX"
                step="1"
                :disabled="fieldsDisabled"
              />
              <span class="auto-key-range-unit">{{ t('koreaConfig.secUnit') }}</span>
            </div>
          </div>
        </template>

        <template v-if="isWildBearMap">
          <div class="section-title">{{ t('koreaConfig.mapSpecificSection') }}</div>
          <p class="section-hint">{{ t('koreaConfig.wildBearMapHint') }}</p>
          <div
            v-for="row in wildBearPatrolRows"
            :key="row.id"
            class="treasure-layer-row"
          >
            <span class="treasure-layer-label">{{ row.label }}</span>
            <div class="treasure-layer-range">
              <input
                v-model.number="wildBearConfig[row.minKey]"
                class="native-input treasure-sec-input"
                type="number"
                :min="row.rangeMin"
                :max="row.rangeMax"
                step="1"
                :disabled="fieldsDisabled"
              />
              <span class="auto-key-range-sep">～</span>
              <input
                v-model.number="wildBearConfig[row.maxKey]"
                class="native-input treasure-sec-input"
                type="number"
                :min="row.rangeMin"
                :max="row.rangeMax"
                step="1"
                :disabled="fieldsDisabled"
              />
              <span class="auto-key-range-unit">{{ t('koreaConfig.countUnit') }}</span>
            </div>
          </div>
          <div class="form-item form-item--attack-hold-ms">
            <label class="form-label">{{ t('koreaConfig.attackHoldDuration') }}</label>
            <div class="auto-key-range attack-hold-range">
              <input
                v-model.number="wildBearConfig.attackHoldMinMs"
                class="native-input treasure-sec-input"
                type="number"
                :min="ATTACK_HOLD_MS_MIN"
                :max="ATTACK_HOLD_MS_MAX"
                step="1"
                :disabled="fieldsDisabled"
              />
              <span class="auto-key-range-sep">～</span>
              <input
                v-model.number="wildBearConfig.attackHoldMaxMs"
                class="native-input treasure-sec-input"
                type="number"
                :min="ATTACK_HOLD_MS_MIN"
                :max="ATTACK_HOLD_MS_MAX"
                step="1"
                :disabled="fieldsDisabled"
              />
              <span class="auto-key-range-unit">{{ t('koreaConfig.msUnit') }}</span>
            </div>
            <p class="field-hint">{{ t('koreaConfig.attackHoldHint', { min: ATTACK_HOLD_MS_MIN, max: ATTACK_HOLD_MS_MAX }) }}</p>
          </div>
        </template>

        <template v-if="isLabC2Map">
          <div class="section-title">{{ t('koreaConfig.mapSpecificSection') }}</div>
          <p class="section-hint">{{ t('koreaConfig.labC2MapHint') }}</p>

          <div class="treasure-layer-row lab-c2-hang-mode-row">
            <span class="treasure-layer-label">{{ t('koreaConfig.labC2HangMode') }}</span>
            <div class="lab-c2-hang-mode-options">
              <label
                v-for="mode in LAB_C2_HANG_MODE_OPTIONS"
                :key="mode.value"
                class="form-check lab-c2-hang-mode-option"
              >
                <input
                  v-model="labC2Config.hangMode"
                  type="radio"
                  class="native-radio"
                  :value="mode.value"
                  :disabled="fieldsDisabled"
                />
                <span>{{ t(mode.labelKey) }}</span>
              </label>
            </div>
          </div>

          <label class="form-check lab-c2-auto-return">
            <input
              v-model="labC2Config.autoReturnMap"
              type="checkbox"
              class="native-checkbox"
              :disabled="fieldsDisabled"
            />
            <span>{{ t('koreaConfig.labC2AutoReturnMap') }}</span>
          </label>

          <template v-if="labC2Config.hangMode === LAB_C2_HANG_MODE_DEFAULT">
            <div
              v-for="row in labC2DurationRows"
              :key="row.id"
              class="treasure-layer-row"
            >
              <span class="treasure-layer-label">{{ row.label }}</span>
              <div class="treasure-layer-range">
                <input
                  v-model.number="labC2Config[row.minKey]"
                  class="native-input treasure-sec-input"
                  type="number"
                  :min="LAB_C2_DURATION_SEC_MIN"
                  :max="LAB_C2_DURATION_SEC_MAX"
                  step="1"
                  :disabled="fieldsDisabled"
                />
                <span class="auto-key-range-sep">～</span>
                <input
                  v-model.number="labC2Config[row.maxKey]"
                  class="native-input treasure-sec-input"
                  type="number"
                  :min="LAB_C2_DURATION_SEC_MIN"
                  :max="LAB_C2_DURATION_SEC_MAX"
                  step="1"
                  :disabled="fieldsDisabled"
                />
                <span class="auto-key-range-unit">{{ t('koreaConfig.secUnit') }}</span>
              </div>
            </div>
          </template>

          <div class="form-item form-item--attack-hold-ms">
            <label class="form-label">{{ t('koreaConfig.attackHoldDuration') }}</label>
            <div class="auto-key-range attack-hold-range">
              <input
                v-model.number="labC2Config.attackHoldMinMs"
                class="native-input treasure-sec-input"
                type="number"
                :min="ATTACK_HOLD_MS_MIN"
                :max="ATTACK_HOLD_MS_MAX"
                step="1"
                :disabled="fieldsDisabled"
              />
              <span class="auto-key-range-sep">～</span>
              <input
                v-model.number="labC2Config.attackHoldMaxMs"
                class="native-input treasure-sec-input"
                type="number"
                :min="ATTACK_HOLD_MS_MIN"
                :max="ATTACK_HOLD_MS_MAX"
                step="1"
                :disabled="fieldsDisabled"
              />
              <span class="auto-key-range-unit">{{ t('koreaConfig.msUnit') }}</span>
            </div>
            <p class="field-hint">{{ t('koreaConfig.attackHoldHint', { min: ATTACK_HOLD_MS_MIN, max: ATTACK_HOLD_MS_MAX }) }}</p>
          </div>

          <div class="treasure-layer-row lab-c2-move-prob-row">
            <span class="treasure-layer-label">{{ t('koreaConfig.labC2MoveProb') }}</span>
            <div class="lab-c2-move-prob-fields">
              <span class="lab-c2-move-prob-item">
                <span class="lab-c2-move-prob-label">{{ t('koreaConfig.labC2WalkProb') }}</span>
                <input
                  v-model.number="labC2Config.walkPercent"
                  class="native-input treasure-sec-input"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  :disabled="fieldsDisabled"
                  @input="onLabC2WalkPercentChange"
                  @change="onLabC2WalkPercentChange"
                />
                <span class="auto-key-range-unit">%</span>
              </span>
              <span class="lab-c2-move-prob-item">
                <span class="lab-c2-move-prob-label">{{ t('koreaConfig.labC2TeleportProb') }}</span>
                <input
                  v-model.number="labC2Config.teleportPercent"
                  class="native-input treasure-sec-input"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  :disabled="fieldsDisabled"
                  @input="onLabC2TeleportPercentChange"
                  @change="onLabC2TeleportPercentChange"
                />
                <span class="auto-key-range-unit">%</span>
              </span>
            </div>
          </div>
          <p class="field-hint lab-c2-move-prob-hint">{{ t('koreaConfig.labC2MoveProbHint') }}</p>
        </template>

        <div class="section-title">{{ t('koreaConfig.otherSection') }}</div>
        <div class="form-item form-item--lie-server">
          <label class="form-label">{{ t('koreaConfig.lieDetectServer') }}</label>
          <input
            v-model="lieDetectServer"
            class="native-input"
            type="text"
            :placeholder="t('koreaConfig.lieDetectServerPlaceholder')"
            :disabled="fieldsDisabled"
          />
          <p class="field-hint">{{ t('koreaConfig.lieDetectServerHint') }}</p>
        </div>
        <div class="check-row">
          <label class="form-check">
            <input
              v-model="centralCommEnabled"
              type="checkbox"
              class="native-checkbox"
              :disabled="fieldsDisabled"
            />
            <span>{{ t('koreaConfig.centralComm') }}</span>
          </label>
          <label class="form-check">
            <input
              v-model="autoClearBag"
              type="checkbox"
              class="native-checkbox"
              :disabled="fieldsDisabled"
            />
            <span>{{ t('koreaConfig.autoClearBag') }}</span>
          </label>
          <label class="form-check">
            <input
              v-model="autoHeal"
              type="checkbox"
              class="native-checkbox"
              :disabled="fieldsDisabled"
            />
            <span>{{ t('koreaConfig.autoHeal') }}</span>
          </label>
        </div>

        <div class="form-item form-item--clear-bag-interval">
          <label class="form-label">{{ t('koreaConfig.autoClearBagInterval') }}</label>
          <div class="auto-key-range clear-bag-interval-range">
            <input
              v-model.number="autoClearBagInterval.minMin"
              class="native-input treasure-sec-input"
              type="number"
              :min="CLEAR_BAG_INTERVAL_MIN"
              :max="CLEAR_BAG_INTERVAL_MAX"
              step="1"
              :disabled="fieldsDisabled || !autoClearBag"
            />
            <span class="auto-key-range-sep">～</span>
            <input
              v-model.number="autoClearBagInterval.maxMin"
              class="native-input treasure-sec-input"
              type="number"
              :min="CLEAR_BAG_INTERVAL_MIN"
              :max="CLEAR_BAG_INTERVAL_MAX"
              step="1"
              :disabled="fieldsDisabled || !autoClearBag"
            />
            <span class="auto-key-range-unit">{{ t('koreaConfig.minuteUnit') }}</span>
          </div>
          <p class="field-hint">{{ t('koreaConfig.autoClearBagIntervalHint', { min: CLEAR_BAG_INTERVAL_MIN, max: CLEAR_BAG_INTERVAL_MAX }) }}</p>
        </div>

        <div class="check-row check-row--auto-medicine">
          <label class="form-check">
            <input
              v-model="autoBuyMedicine.enabled"
              type="checkbox"
              class="native-checkbox"
              :disabled="fieldsDisabled"
            />
            <span>{{ t('koreaConfig.autoBuyMedicine') }}</span>
          </label>
          <label
            v-for="med in KOREA_MEDICINE_OPTIONS"
            :key="med.key"
            class="form-check"
          >
            <input
              v-model="autoBuyMedicine[med.key]"
              type="checkbox"
              class="native-checkbox"
              :disabled="fieldsDisabled || !autoBuyMedicine.enabled"
            />
            <span>{{ t(med.labelKey) }}</span>
          </label>
        </div>

        <div class="form-item form-item--teleport-attack-ms">
          <label class="form-label">{{ t('koreaConfig.teleportAttackInterval') }}</label>
          <div class="auto-key-range teleport-attack-range">
            <input
              v-model.number="teleportAttackInterval.minMs"
              class="native-input treasure-sec-input"
              type="number"
              :min="TELEPORT_ATTACK_MS_MIN"
              :max="TELEPORT_ATTACK_MS_MAX"
              step="1"
              :disabled="fieldsDisabled"
            />
            <span class="auto-key-range-sep">～</span>
            <input
              v-model.number="teleportAttackInterval.maxMs"
              class="native-input treasure-sec-input"
              type="number"
              :min="TELEPORT_ATTACK_MS_MIN"
              :max="TELEPORT_ATTACK_MS_MAX"
              step="1"
              :disabled="fieldsDisabled"
            />
            <span class="auto-key-range-unit">{{ t('koreaConfig.msUnit') }}</span>
          </div>
          <p class="field-hint">{{ t('koreaConfig.teleportAttackIntervalHint', { min: TELEPORT_ATTACK_MS_MIN, max: TELEPORT_ATTACK_MS_MAX }) }}</p>
        </div>

        <div class="form-item form-item--scheduled-logout">
          <label class="form-check scheduled-logout-enable">
            <input
              v-model="scheduledLogout.enabled"
              type="checkbox"
              class="native-checkbox"
              :disabled="fieldsDisabled"
            />
            <span>{{ t('koreaConfig.scheduledLogout') }}</span>
          </label>
          <div class="scheduled-logout-fields">
            <div class="scheduled-logout-row">
              <span class="scheduled-logout-inline-label">{{ t('koreaConfig.scheduledLogoutEvery') }}</span>
              <input
                v-model.number="scheduledLogout.intervalHours"
                class="native-input treasure-sec-input"
                type="number"
                :min="SCHEDULED_LOGOUT_INTERVAL_HOUR_MIN"
                :max="SCHEDULED_LOGOUT_INTERVAL_HOUR_MAX"
                step="1"
                :disabled="fieldsDisabled || !scheduledLogout.enabled"
              />
              <span class="auto-key-range-unit">{{ t('koreaConfig.hourUnit') }}</span>
              <span class="scheduled-logout-inline-label">{{ t('koreaConfig.scheduledLogoutDuration') }}</span>
              <input
                v-model.number="scheduledLogout.durationMin"
                class="native-input treasure-sec-input"
                type="number"
                :min="SCHEDULED_LOGOUT_DURATION_MIN"
                :max="SCHEDULED_LOGOUT_DURATION_MAX"
                step="1"
                :disabled="fieldsDisabled || !scheduledLogout.enabled"
              />
              <span class="auto-key-range-unit">{{ t('koreaConfig.minuteUnit') }}</span>
            </div>
            <div class="scheduled-logout-row scheduled-logout-row--position">
              <span class="scheduled-logout-inline-label">{{ t('koreaConfig.scheduledLogoutPosition') }}</span>
              <label
                v-for="pos in SCHEDULED_LOGOUT_POSITION_OPTIONS"
                :key="pos.value"
                class="form-check scheduled-logout-position"
              >
                <input
                  v-model.number="scheduledLogout.position"
                  type="radio"
                  class="native-radio"
                  :value="pos.value"
                  :disabled="fieldsDisabled || !scheduledLogout.enabled"
                />
                <span>{{ t(pos.labelKey) }}</span>
              </label>
            </div>
          </div>
          <p class="field-hint">{{ t('koreaConfig.scheduledLogoutHint', { hourMin: SCHEDULED_LOGOUT_INTERVAL_HOUR_MIN, hourMax: SCHEDULED_LOGOUT_INTERVAL_HOUR_MAX, minMin: SCHEDULED_LOGOUT_DURATION_MIN, minMax: SCHEDULED_LOGOUT_DURATION_MAX }) }}</p>
        </div>

        <div class="section-title">{{ t('koreaConfig.autoKeySection') }}</div>
        <p class="section-hint">
          {{ t('koreaConfig.autoKeyHint', { min: SEC_MIN, max: SEC_MAX }) }}
        </p>
        <div class="auto-key-list">
          <div
            v-for="(slot, index) in autoKeySlots"
            :key="slot.presetId || `custom-${index}`"
            class="auto-key-row"
            :class="{ 'auto-key-row--locked': slot.locked }"
          >
            <div class="auto-key-row-fields">
              <label class="form-check auto-key-enable">
                <input
                  v-model="slot.enabled"
                  type="checkbox"
                  class="native-checkbox"
                  :disabled="fieldsDisabled"
                />
                <span>{{ t('common.enable') }}</span>
              </label>
              <input
                v-model="slot.name"
                class="native-input auto-key-name"
                type="text"
                :placeholder="t('koreaConfig.remarkName')"
                :disabled="fieldsDisabled || !slot.enabled || slot.locked"
                :readonly="slot.locked"
              />
              <select
                v-model="slot.key"
                class="native-select auto-key-letter"
                :disabled="fieldsDisabled || !slot.enabled || isKeyLocked(slot)"
              >
                <option v-for="k in KEY_SELECT_OPTIONS" :key="k" :value="k">{{ k }}</option>
              </select>
              <div v-if="slot.mode === 'interval'" class="auto-key-range">
                <input
                  v-model.number="slot.minSec"
                  class="native-input auto-key-sec"
                  type="number"
                  :min="SEC_MIN"
                  :max="SEC_MAX"
                  step="1"
                  :disabled="fieldsDisabled || !slot.enabled"
                />
                <span class="auto-key-range-sep">～</span>
                <input
                  v-model.number="slot.maxSec"
                  class="native-input auto-key-sec"
                  type="number"
                  :min="SEC_MIN"
                  :max="SEC_MAX"
                  step="1"
                  :disabled="fieldsDisabled || !slot.enabled"
                />
                <span class="auto-key-range-unit">{{ t('koreaConfig.secUnit') }}</span>
              </div>
              <span v-else class="auto-key-instant-tag">{{ t('koreaConfig.instantKey') }}</span>
            </div>
            <button
              v-if="!isViewMode && !(isEditMode && isDefaultConfig) && !slot.locked"
              type="button"
              class="text-btn text-btn--danger auto-key-delete-btn"
              @click="removeAutoKeySlot(index)"
            >
              {{ t('common.delete') }}
            </button>
          </div>
        </div>
        <button
          v-if="!isViewMode && !(isEditMode && isDefaultConfig) && canAddCustomKey"
          type="button"
          class="text-btn text-btn--add"
          @click="addAutoKeySlot"
        >
          {{ t('koreaConfig.addTimedKey', { count: customKeyCount, max: MAX_CUSTOM_KEYS }) }}
        </button>
      </div>
    </div>

    <div v-if="!isViewMode" class="bottom-actions">
      <button
        class="primary-btn"
        :disabled="saving"
        @click="saveConfig"
      >
        {{ saving ? t('common.saving') : (isEditMode ? t('koreaConfig.updateConfig') : t('koreaConfig.saveConfig')) }}
      </button>
    </div>
    <div v-else class="view-mode-hint">
      {{ t('koreaConfig.viewOnly') }}
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { t } from '@/locales/zh'
import { ElMessage } from 'element-plus'
import { getConfig, createConfig, updateConfig } from '@/api/configs'
import { getAdventureHangMapOptions } from '@/api/adventureHangMaps'

const GAME_NAME = '冒险岛韩服'
const KEY_OPTIONS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')
const SHIFT_KEY_OPTIONS = ['左Shift', '右Shift']
const HOTKEY_OPTIONS = ['快捷键1', '快捷键2', '快捷键3', '快捷键4']
const KEY_SELECT_OPTIONS = [...KEY_OPTIONS, '空格', ...SHIFT_KEY_OPTIONS, ...HOTKEY_OPTIONS]
const SEC_MIN = 30
const SEC_MAX = 1999
const FIGHT_SEC_MIN = 1
const FIGHT_SEC_MAX = 9999
const TELEPORT_ATTACK_MS_MIN = 50
const TELEPORT_ATTACK_MS_MAX = 1000
const TELEPORT_ATTACK_MS_DEFAULT_MIN = 50
const TELEPORT_ATTACK_MS_DEFAULT_MAX = 80
const CLEAR_BAG_INTERVAL_MIN = 1
const CLEAR_BAG_INTERVAL_MAX = 1440
const CLEAR_BAG_INTERVAL_DEFAULT_MIN = 30
const CLEAR_BAG_INTERVAL_DEFAULT_MAX = 40
const SCHEDULED_LOGOUT_INTERVAL_HOUR_MIN = 1
const SCHEDULED_LOGOUT_INTERVAL_HOUR_MAX = 24
const SCHEDULED_LOGOUT_DURATION_MIN = 1
const SCHEDULED_LOGOUT_DURATION_MAX = 600
const SCHEDULED_LOGOUT_DEFAULT_INTERVAL_HOURS = 8
const SCHEDULED_LOGOUT_DEFAULT_DURATION_MIN = 60
const SCHEDULED_LOGOUT_DEFAULT_POSITION = 1
const SCHEDULED_LOGOUT_POSITION_OPTIONS = [
  { value: 1, labelKey: 'koreaConfig.scheduledLogoutPosition1' },
  { value: 2, labelKey: 'koreaConfig.scheduledLogoutPosition2' },
  { value: 3, labelKey: 'koreaConfig.scheduledLogoutPosition3' }
]
const TREASURE_ISLAND_MAP_KEYWORD = '韩服抢夺宝物岛'
const WILD_BEAR_MAP_KEYWORD = '野熊的领地'
const LAB_C2_MAP_KEYWORD = '韩服研究所C2'
const LAB_C2_HANG_MODE_DEFAULT = '默认'
const LAB_C2_HANG_MODE_PLATFORM = '站台站撸'
const LAB_C2_HANG_MODE_PATROL = '全图巡逻'
const LAB_C2_HANG_MODE_OPTIONS = [
  { value: LAB_C2_HANG_MODE_DEFAULT, labelKey: 'koreaConfig.labC2HangDefault' },
  { value: LAB_C2_HANG_MODE_PLATFORM, labelKey: 'koreaConfig.labC2HangPlatform' },
  { value: LAB_C2_HANG_MODE_PATROL, labelKey: 'koreaConfig.labC2HangPatrol' }
]
const LAB_C2_DURATION_SEC_MIN = 1
const LAB_C2_DURATION_SEC_MAX = 9999
const LAB_C2_PATROL_DEFAULT_MIN = 20
const LAB_C2_PATROL_DEFAULT_MAX = 30
const LAB_C2_STAND_DEFAULT_MIN = 20
const LAB_C2_STAND_DEFAULT_MAX = 30
const LAB_C2_WALK_PERCENT_DEFAULT = 10
const LAB_C2_TELEPORT_PERCENT_DEFAULT = 90
const MID_PATROL_MIN = 1
const MID_PATROL_MAX = 4
const BOTTOM_PATROL_MIN = 1
const BOTTOM_PATROL_MAX = 5
const ATTACK_HOLD_MS_MIN = 100
const ATTACK_HOLD_MS_MAX = 2000
const MID_PATROL_DEFAULT_MIN = 1
const MID_PATROL_DEFAULT_MAX = 1
const BOTTOM_PATROL_DEFAULT_MIN = 2
const BOTTOM_PATROL_DEFAULT_MAX = 2
const ATTACK_HOLD_MS_DEFAULT_MIN = 300
const ATTACK_HOLD_MS_DEFAULT_MAX = 400
const MODE_INTERVAL = 'interval'
const MODE_INSTANT = 'instant'

const PRESET_DEFS = [
  {
    presetId: 'pet-potion',
    nameKey: 'koreaConfig.presetPet',
    locked: true,
    lockKey: false,
    enabled: true,
    key: 'N',
    mode: MODE_INTERVAL,
    minSec: 600,
    maxSec: 700
  },
  {
    presetId: 'jump',
    nameKey: 'koreaConfig.presetJump',
    locked: true,
    enabled: true,
    key: 'Z',
    mode: MODE_INSTANT
  },
  {
    presetId: 'teleport',
    nameKey: 'koreaConfig.presetTeleport',
    locked: true,
    enabled: true,
    key: 'X',
    mode: MODE_INSTANT
  },
  {
    presetId: 'attack',
    nameKey: 'koreaConfig.presetAttack',
    locked: true,
    lockKey: false,
    enabled: true,
    key: '空格',
    mode: MODE_INSTANT
  }
]

const PRESET_LEGACY_ZH_NAMES = {
  'pet-potion': '宠物药水',
  jump: '跳跃键',
  teleport: '瞬移键',
  attack: '攻击键'
}

const MAX_CUSTOM_KEYS = 7
const MAX_AUTO_KEYS = PRESET_DEFS.length + MAX_CUSTOM_KEYS

const KOREA_MEDICINE_OPTIONS = [
  { key: 'whitePotion', labelKey: 'koreaConfig.medicineWhite', jsonKey: '白血瓶' },
  { key: 'bluePotion', labelKey: 'koreaConfig.medicineBlue', jsonKey: '蓝血瓶' },
  { key: 'petSnack', labelKey: 'koreaConfig.medicinePetSnack', jsonKey: '宠物零食' }
]

function defaultAutoBuyMedicine() {
  return {
    enabled: false,
    whitePotion: true,
    bluePotion: true,
    petSnack: true
  }
}

function parseAutoBuyMedicine(content) {
  const d = defaultAutoBuyMedicine()
  const raw = content?.['自动买药']
  if (typeof raw === 'boolean') {
    d.enabled = parseEnabled(raw)
    return d
  }
  if (!raw || typeof raw !== 'object') return d
  if (raw.启用 !== undefined) d.enabled = parseEnabled(raw.启用)
  if (raw.白血瓶 !== undefined) d.whitePotion = parseEnabled(raw.白血瓶)
  if (raw.蓝血瓶 !== undefined) d.bluePotion = parseEnabled(raw.蓝血瓶)
  if (raw.宠物零食 !== undefined) d.petSnack = parseEnabled(raw.宠物零食)
  return d
}

function serializeAutoBuyMedicine(state) {
  return {
    启用: !!state.enabled,
    白血瓶: !!state.whitePotion,
    蓝血瓶: !!state.bluePotion,
    宠物零食: !!state.petSnack
  }
}

function defaultTeleportAttackInterval() {
  return {
    minMs: TELEPORT_ATTACK_MS_DEFAULT_MIN,
    maxMs: TELEPORT_ATTACK_MS_DEFAULT_MAX
  }
}

function normalizeMs(n) {
  const v = Math.floor(Number(n))
  if (!Number.isFinite(v)) return null
  return v
}

function parseTeleportAttackInterval(content) {
  const d = defaultTeleportAttackInterval()
  if (!content || typeof content !== 'object') return d
  return {
    minMs: normalizeMs(content['瞬移和攻击间隔最短毫秒']) ?? d.minMs,
    maxMs: normalizeMs(content['瞬移和攻击间隔最长毫秒']) ?? d.maxMs
  }
}

function serializeTeleportAttackInterval(interval) {
  const d = defaultTeleportAttackInterval()
  const f = interval || d
  return {
    瞬移和攻击间隔最短毫秒: normalizeMs(f.minMs) ?? d.minMs,
    瞬移和攻击间隔最长毫秒: normalizeMs(f.maxMs) ?? d.maxMs
  }
}

function validateTeleportAttackInterval(interval, t) {
  const label = t('koreaConfig.teleportAttackInterval')
  const minMs = normalizeMs(interval.minMs)
  const maxMs = normalizeMs(interval.maxMs)
  if (minMs == null || maxMs == null) {
    return t('koreaConfig.intervalRequired', { label })
  }
  if (
    minMs < TELEPORT_ATTACK_MS_MIN ||
    maxMs < TELEPORT_ATTACK_MS_MIN ||
    minMs > TELEPORT_ATTACK_MS_MAX ||
    maxMs > TELEPORT_ATTACK_MS_MAX
  ) {
    return t('koreaConfig.intervalRange', {
      label,
      min: TELEPORT_ATTACK_MS_MIN,
      max: TELEPORT_ATTACK_MS_MAX
    })
  }
  if (minMs > maxMs) {
    return t('koreaConfig.intervalOrder', { label })
  }
  return null
}

function defaultAutoClearBagInterval() {
  return {
    minMin: CLEAR_BAG_INTERVAL_DEFAULT_MIN,
    maxMin: CLEAR_BAG_INTERVAL_DEFAULT_MAX
  }
}

function parseAutoClearBagInterval(content) {
  const d = defaultAutoClearBagInterval()
  if (!content || typeof content !== 'object') return d
  return {
    minMin: normalizeSec(content['自动清包间隔最短分钟']) ?? d.minMin,
    maxMin: normalizeSec(content['自动清包间隔最长分钟']) ?? d.maxMin
  }
}

function serializeAutoClearBagInterval(interval) {
  const d = defaultAutoClearBagInterval()
  const f = interval || d
  return {
    自动清包间隔最短分钟: normalizeSec(f.minMin) ?? d.minMin,
    自动清包间隔最长分钟: normalizeSec(f.maxMin) ?? d.maxMin
  }
}

function validateAutoClearBagInterval(interval, t) {
  const label = t('koreaConfig.autoClearBagInterval')
  const minMin = normalizeSec(interval.minMin)
  const maxMin = normalizeSec(interval.maxMin)
  if (minMin == null || maxMin == null) {
    return t('koreaConfig.intervalRequired', { label })
  }
  if (
    minMin < CLEAR_BAG_INTERVAL_MIN ||
    maxMin < CLEAR_BAG_INTERVAL_MIN ||
    minMin > CLEAR_BAG_INTERVAL_MAX ||
    maxMin > CLEAR_BAG_INTERVAL_MAX
  ) {
    return t('koreaConfig.intervalRange', {
      label,
      min: CLEAR_BAG_INTERVAL_MIN,
      max: CLEAR_BAG_INTERVAL_MAX
    })
  }
  if (minMin > maxMin) {
    return t('koreaConfig.intervalOrder', { label })
  }
  return null
}

function defaultScheduledLogout() {
  return {
    enabled: false,
    intervalHours: SCHEDULED_LOGOUT_DEFAULT_INTERVAL_HOURS,
    durationMin: SCHEDULED_LOGOUT_DEFAULT_DURATION_MIN,
    position: SCHEDULED_LOGOUT_DEFAULT_POSITION
  }
}

function normalizeScheduledLogoutPosition(raw) {
  const v = normalizeSec(raw)
  if (v == null) return null
  if (v >= 1 && v <= 3) return v
  return null
}

function parseScheduledLogout(content) {
  const d = defaultScheduledLogout()
  const raw = content?.['定时下线']
  if (typeof raw === 'boolean') {
    d.enabled = parseEnabled(raw)
  } else if (raw && typeof raw === 'object') {
    if (raw.启用 !== undefined) d.enabled = parseEnabled(raw.启用)
    if (raw.间隔小时 !== undefined) {
      d.intervalHours = normalizeSec(raw.间隔小时) ?? d.intervalHours
    }
    if (raw.下线分钟 !== undefined) {
      d.durationMin = normalizeSec(raw.下线分钟) ?? d.durationMin
    }
    if (raw.角色位置 !== undefined) {
      d.position = normalizeScheduledLogoutPosition(raw.角色位置) ?? d.position
    }
  }
  if (content?.['定时下线间隔小时'] !== undefined) {
    d.intervalHours = normalizeSec(content['定时下线间隔小时']) ?? d.intervalHours
  }
  if (content?.['定时下线时长分钟'] !== undefined) {
    d.durationMin = normalizeSec(content['定时下线时长分钟']) ?? d.durationMin
  }
  if (content?.['角色位置'] !== undefined) {
    d.position = normalizeScheduledLogoutPosition(content['角色位置']) ?? d.position
  }
  return d
}

function serializeScheduledLogout(state) {
  const d = defaultScheduledLogout()
  const s = state || d
  return {
    定时下线: !!s.enabled,
    定时下线间隔小时: normalizeSec(s.intervalHours) ?? d.intervalHours,
    定时下线时长分钟: normalizeSec(s.durationMin) ?? d.durationMin,
    角色位置: normalizeScheduledLogoutPosition(s.position) ?? d.position
  }
}

function validateNumericField(value, label, min, max, t) {
  const v = normalizeSec(value)
  if (v == null) {
    return t('koreaConfig.numericRequired', { label })
  }
  if (v < min || v > max) {
    return t('koreaConfig.numericRange', { label, min, max })
  }
  return null
}

function validateScheduledLogout(state, t) {
  const intervalLabel = t('koreaConfig.scheduledLogoutIntervalLabel')
  const durationLabel = t('koreaConfig.scheduledLogoutDurationLabel')
  const intervalErr = validateNumericField(
    state.intervalHours,
    intervalLabel,
    SCHEDULED_LOGOUT_INTERVAL_HOUR_MIN,
    SCHEDULED_LOGOUT_INTERVAL_HOUR_MAX,
    t
  )
  if (intervalErr) return intervalErr
  const durationErr = validateNumericField(
    state.durationMin,
    durationLabel,
    SCHEDULED_LOGOUT_DURATION_MIN,
    SCHEDULED_LOGOUT_DURATION_MAX,
    t
  )
  if (durationErr) return durationErr
  const positionLabel = t('koreaConfig.scheduledLogoutPosition')
  if (normalizeScheduledLogoutPosition(state.position) == null) {
    return t('koreaConfig.scheduledLogoutPositionRequired', { label: positionLabel })
  }
  return null
}

function parseEnabled(raw) {
  return raw === true || raw === 1 || raw === '1' || raw === 'true'
}

function normalizeSec(n) {
  const v = Math.floor(Number(n))
  if (!Number.isFinite(v)) return null
  return v
}

function normalizeKey(raw) {
  const s = String(raw ?? '').trim()
  if (s === '空格' || s.toUpperCase() === 'SPACE') return '空格'
  if (SHIFT_KEY_OPTIONS.includes(s)) return s
  if (HOTKEY_OPTIONS.includes(s)) return s
  const k = s.toUpperCase()
  return KEY_OPTIONS.includes(k) ? k : ''
}

function isKeyLocked(slot) {
  if (!slot?.locked) return false
  return slot.lockKey !== false
}

function resolveSlotKey(slot) {
  if (isKeyLocked(slot)) return slot.key
  return normalizeKey(slot.key) || 'A'
}

function parseKeyMode(item) {
  const modeRaw = item.模式 ?? item.mode
  if (modeRaw === '即时' || modeRaw === MODE_INSTANT) return MODE_INSTANT
  if (modeRaw === '定时' || modeRaw === MODE_INTERVAL) return MODE_INTERVAL
  const hasInterval =
    item.间隔最短秒 != null ||
    item.间隔最长秒 != null ||
    item.最短秒 != null ||
    item.最长秒 != null ||
    item.minSec != null ||
    item.maxSec != null
  return hasInterval ? MODE_INTERVAL : MODE_INSTANT
}

function slotFromRaw(item, preset) {
  const mode = preset?.mode ?? parseKeyMode(item)
  const loadedKey = normalizeKey(item?.按键 ?? item?.key)
  const slotLocked = !!preset?.locked
  const slotLockKey = preset?.lockKey
  const key = isKeyLocked({ locked: slotLocked, lockKey: slotLockKey, key: preset?.key })
    ? preset?.key || 'A'
    : loadedKey || preset?.key || 'A'
  return {
    presetId: preset?.presetId || null,
    locked: slotLocked,
    lockKey: slotLockKey,
    enabled: item?.启用 !== undefined ? parseEnabled(item.启用) : parseEnabled(item?.enabled ?? preset?.enabled),
    name: String(item?.名称 ?? item?.name ?? preset?.name ?? '').trim(),
    key,
    mode,
    minSec: normalizeSec(item?.间隔最短秒 ?? item?.最短秒 ?? item?.minSec) ?? preset?.minSec ?? 60,
    maxSec: normalizeSec(item?.间隔最长秒 ?? item?.最长秒 ?? item?.maxSec) ?? preset?.maxSec ?? 90
  }
}

function defaultAutoKeySlot(overrides = {}) {
  return {
    presetId: null,
    locked: false,
    enabled: false,
    name: '',
    key: 'A',
    mode: MODE_INTERVAL,
    minSec: 60,
    maxSec: 90,
    ...overrides
  }
}

function defaultAutoKeySlots(t) {
  return PRESET_DEFS.map((p) =>
    defaultAutoKeySlot({
      ...p,
      name: t(p.nameKey)
    })
  )
}

function parseAutoKeySlots(raw, t) {
  const loaded = Array.isArray(raw) ? raw : []
  const used = new Set()

  const findPresetItem = (preset) => {
    const legacy = PRESET_LEGACY_ZH_NAMES[preset.presetId]
    const localized = t(preset.nameKey)
    for (let i = 0; i < loaded.length; i++) {
      if (used.has(i)) continue
      const item = loaded[i]
      if (!item || typeof item !== 'object') continue
      const name = String(item.名称 ?? item.name ?? '').trim()
      if (name === localized || name === legacy) {
        used.add(i)
        return item
      }
      // 兼容旧数据：无名称时按内置项按键+模式匹配
      if (
        preset.locked &&
        !name &&
        normalizeKey(item.按键 ?? item.key) === preset.key &&
        parseKeyMode(item) === preset.mode
      ) {
        used.add(i)
        return item
      }
    }
    return null
  }

  const merged = PRESET_DEFS.map((preset) => {
    const item = findPresetItem(preset)
    return slotFromRaw(item || {}, {
      ...preset,
      name: t(preset.nameKey)
    })
  })

  // 其余条目均为自定义按键（含名称为空的）
  for (let i = 0; i < loaded.length; i++) {
    if (used.has(i)) continue
    const item = loaded[i]
    if (!item || typeof item !== 'object') continue
    merged.push(slotFromRaw(item, null))
  }

  return merged.slice(0, MAX_AUTO_KEYS)
}

function serializeAutoKeySlots(slots) {
  let customIdx = 0
  return (slots || []).slice(0, MAX_AUTO_KEYS).map((s) => {
    let name = String(s.name ?? '').trim()
    if (s.presetId && PRESET_LEGACY_ZH_NAMES[s.presetId]) {
      // 内置项固定写中文名，供脚本端识别
      name = PRESET_LEGACY_ZH_NAMES[s.presetId]
    } else if (!name) {
      customIdx += 1
      name = `自定义按键${customIdx}`
    }
    const row = {
      启用: !!s.enabled,
      名称: name,
      按键: resolveSlotKey(s),
      模式: s.mode === MODE_INSTANT ? '即时' : '定时'
    }
    if (s.mode === MODE_INTERVAL) {
      row.间隔最短秒 = normalizeSec(s.minSec) ?? 60
      row.间隔最长秒 = normalizeSec(s.maxSec) ?? 90
    }
    return row
  })
}

function isTreasureIslandHangMap(mapValue) {
  return String(mapValue ?? '').includes(TREASURE_ISLAND_MAP_KEYWORD)
}

function isWildBearHangMap(mapValue) {
  return String(mapValue ?? '').includes(WILD_BEAR_MAP_KEYWORD)
}

function defaultWildBearConfig() {
  return {
    midPatrolMin: MID_PATROL_DEFAULT_MIN,
    midPatrolMax: MID_PATROL_DEFAULT_MAX,
    bottomPatrolMin: BOTTOM_PATROL_DEFAULT_MIN,
    bottomPatrolMax: BOTTOM_PATROL_DEFAULT_MAX,
    attackHoldMinMs: ATTACK_HOLD_MS_DEFAULT_MIN,
    attackHoldMaxMs: ATTACK_HOLD_MS_DEFAULT_MAX
  }
}

function parseWildBearConfig(content) {
  const d = defaultWildBearConfig()
  if (!content || typeof content !== 'object') return d
  return {
    midPatrolMin: normalizeSec(content['中层巡逻最少次数']) ?? d.midPatrolMin,
    midPatrolMax: normalizeSec(content['中层巡逻最多次数']) ?? d.midPatrolMax,
    bottomPatrolMin: normalizeSec(content['下层巡逻最少次数']) ?? d.bottomPatrolMin,
    bottomPatrolMax: normalizeSec(content['下层巡逻最多次数']) ?? d.bottomPatrolMax,
    attackHoldMinMs: normalizeMs(content['攻击长按最短毫秒']) ?? d.attackHoldMinMs,
    attackHoldMaxMs: normalizeMs(content['攻击长按最长毫秒']) ?? d.attackHoldMaxMs
  }
}

function serializeWildBearConfig(config) {
  const d = defaultWildBearConfig()
  const c = config || d
  return {
    中层巡逻最少次数: normalizeSec(c.midPatrolMin) ?? d.midPatrolMin,
    中层巡逻最多次数: normalizeSec(c.midPatrolMax) ?? d.midPatrolMax,
    下层巡逻最少次数: normalizeSec(c.bottomPatrolMin) ?? d.bottomPatrolMin,
    下层巡逻最多次数: normalizeSec(c.bottomPatrolMax) ?? d.bottomPatrolMax,
    攻击长按最短毫秒: normalizeMs(c.attackHoldMinMs) ?? d.attackHoldMinMs,
    攻击长按最长毫秒: normalizeMs(c.attackHoldMaxMs) ?? d.attackHoldMaxMs
  }
}

function validateWildBearConfig(config, t) {
  const patrolRows = [
    {
      labelKey: 'koreaConfig.wildBearMidPatrol',
      minKey: 'midPatrolMin',
      maxKey: 'midPatrolMax',
      rangeMin: MID_PATROL_MIN,
      rangeMax: MID_PATROL_MAX
    },
    {
      labelKey: 'koreaConfig.wildBearBottomPatrol',
      minKey: 'bottomPatrolMin',
      maxKey: 'bottomPatrolMax',
      rangeMin: BOTTOM_PATROL_MIN,
      rangeMax: BOTTOM_PATROL_MAX
    }
  ]
  for (const row of patrolRows) {
    const label = t(row.labelKey)
    const minVal = normalizeSec(config[row.minKey])
    const maxVal = normalizeSec(config[row.maxKey])
    if (minVal == null || maxVal == null) {
      return t('koreaConfig.intervalRequired', { label })
    }
    if (
      minVal < row.rangeMin ||
      maxVal < row.rangeMin ||
      minVal > row.rangeMax ||
      maxVal > row.rangeMax
    ) {
      return t('koreaConfig.intervalRange', {
        label,
        min: row.rangeMin,
        max: row.rangeMax
      })
    }
    if (minVal > maxVal) {
      return t('koreaConfig.intervalOrder', { label })
    }
  }

  const holdLabel = t('koreaConfig.attackHoldDuration')
  const minMs = normalizeMs(config.attackHoldMinMs)
  const maxMs = normalizeMs(config.attackHoldMaxMs)
  if (minMs == null || maxMs == null) {
    return t('koreaConfig.intervalRequired', { label: holdLabel })
  }
  if (
    minMs < ATTACK_HOLD_MS_MIN ||
    maxMs < ATTACK_HOLD_MS_MIN ||
    minMs > ATTACK_HOLD_MS_MAX ||
    maxMs > ATTACK_HOLD_MS_MAX
  ) {
    return t('koreaConfig.intervalRange', {
      label: holdLabel,
      min: ATTACK_HOLD_MS_MIN,
      max: ATTACK_HOLD_MS_MAX
    })
  }
  if (minMs > maxMs) {
    return t('koreaConfig.intervalOrder', { label: holdLabel })
  }
  return null
}

function isLabC2HangMap(mapValue) {
  return String(mapValue ?? '').includes(LAB_C2_MAP_KEYWORD)
}

function defaultLabC2Config() {
  return {
    hangMode: LAB_C2_HANG_MODE_DEFAULT,
    autoReturnMap: false,
    patrolMinSec: LAB_C2_PATROL_DEFAULT_MIN,
    patrolMaxSec: LAB_C2_PATROL_DEFAULT_MAX,
    standMinSec: LAB_C2_STAND_DEFAULT_MIN,
    standMaxSec: LAB_C2_STAND_DEFAULT_MAX,
    attackHoldMinMs: ATTACK_HOLD_MS_DEFAULT_MIN,
    attackHoldMaxMs: ATTACK_HOLD_MS_DEFAULT_MAX,
    walkPercent: LAB_C2_WALK_PERCENT_DEFAULT,
    teleportPercent: LAB_C2_TELEPORT_PERCENT_DEFAULT
  }
}

function normalizePercent(raw) {
  const v = normalizeSec(raw)
  if (v == null || v < 0 || v > 100) return null
  return v
}

function clampMovePercent(raw) {
  const v = normalizePercent(raw)
  return v == null ? 0 : v
}

function syncLabC2MoveProbFromWalk(config) {
  const walk = clampMovePercent(config.walkPercent)
  config.walkPercent = walk
  config.teleportPercent = 100 - walk
}

function syncLabC2MoveProbFromTeleport(config) {
  const teleport = clampMovePercent(config.teleportPercent)
  config.teleportPercent = teleport
  config.walkPercent = 100 - teleport
}

function parseLabC2HangMode(raw) {
  const s = String(raw ?? '').trim()
  if (LAB_C2_HANG_MODE_OPTIONS.some((m) => m.value === s)) return s
  return null
}

function parseLabC2Config(content) {
  const d = defaultLabC2Config()
  if (!content || typeof content !== 'object') return d
  const parsed = {
    hangMode: parseLabC2HangMode(content['挂机选项']) ?? d.hangMode,
    autoReturnMap:
      content['自动回图'] === undefined ? d.autoReturnMap : parseEnabled(content['自动回图']),
    patrolMinSec: normalizeSec(content['巡逻时长最短秒']) ?? d.patrolMinSec,
    patrolMaxSec: normalizeSec(content['巡逻时长最长秒']) ?? d.patrolMaxSec,
    standMinSec: normalizeSec(content['站撸时长最短秒']) ?? d.standMinSec,
    standMaxSec: normalizeSec(content['站撸时长最长秒']) ?? d.standMaxSec,
    attackHoldMinMs: normalizeMs(content['攻击长按最短毫秒']) ?? d.attackHoldMinMs,
    attackHoldMaxMs: normalizeMs(content['攻击长按最长毫秒']) ?? d.attackHoldMaxMs,
    walkPercent: normalizePercent(content['走路概率']) ?? d.walkPercent,
    teleportPercent: normalizePercent(content['瞬移概率']) ?? d.teleportPercent
  }
  if (parsed.walkPercent + parsed.teleportPercent !== 100) {
    syncLabC2MoveProbFromWalk(parsed)
  }
  return parsed
}

function serializeLabC2Config(config) {
  const d = defaultLabC2Config()
  const c = config || d
  return {
    挂机选项: parseLabC2HangMode(c.hangMode) ?? d.hangMode,
    自动回图: !!c.autoReturnMap,
    巡逻时长最短秒: normalizeSec(c.patrolMinSec) ?? d.patrolMinSec,
    巡逻时长最长秒: normalizeSec(c.patrolMaxSec) ?? d.patrolMaxSec,
    站撸时长最短秒: normalizeSec(c.standMinSec) ?? d.standMinSec,
    站撸时长最长秒: normalizeSec(c.standMaxSec) ?? d.standMaxSec,
    攻击长按最短毫秒: normalizeMs(c.attackHoldMinMs) ?? d.attackHoldMinMs,
    攻击长按最长毫秒: normalizeMs(c.attackHoldMaxMs) ?? d.attackHoldMaxMs,
    走路概率: normalizePercent(c.walkPercent) ?? d.walkPercent,
    瞬移概率: normalizePercent(c.teleportPercent) ?? d.teleportPercent
  }
}

function validateLabC2DurationRange(config, labelKey, minKey, maxKey, t) {
  const label = t(labelKey)
  const minVal = normalizeSec(config[minKey])
  const maxVal = normalizeSec(config[maxKey])
  if (minVal == null || maxVal == null) {
    return t('koreaConfig.intervalRequired', { label })
  }
  if (
    minVal < LAB_C2_DURATION_SEC_MIN ||
    maxVal < LAB_C2_DURATION_SEC_MIN ||
    minVal > LAB_C2_DURATION_SEC_MAX ||
    maxVal > LAB_C2_DURATION_SEC_MAX
  ) {
    return t('koreaConfig.intervalRange', {
      label,
      min: LAB_C2_DURATION_SEC_MIN,
      max: LAB_C2_DURATION_SEC_MAX
    })
  }
  if (minVal > maxVal) {
    return t('koreaConfig.intervalOrder', { label })
  }
  return null
}

function validateLabC2Config(config, t) {
  if (parseLabC2HangMode(config.hangMode) == null) {
    return t('koreaConfig.labC2HangModeRequired')
  }

  if (config.hangMode === LAB_C2_HANG_MODE_DEFAULT) {
    const patrolErr = validateLabC2DurationRange(
      config,
      'koreaConfig.labC2PatrolDuration',
      'patrolMinSec',
      'patrolMaxSec',
      t
    )
    if (patrolErr) return patrolErr
    const standErr = validateLabC2DurationRange(
      config,
      'koreaConfig.labC2StandDuration',
      'standMinSec',
      'standMaxSec',
      t
    )
    if (standErr) return standErr
  }

  const holdLabel = t('koreaConfig.attackHoldDuration')
  const minMs = normalizeMs(config.attackHoldMinMs)
  const maxMs = normalizeMs(config.attackHoldMaxMs)
  if (minMs == null || maxMs == null) {
    return t('koreaConfig.intervalRequired', { label: holdLabel })
  }
  if (
    minMs < ATTACK_HOLD_MS_MIN ||
    maxMs < ATTACK_HOLD_MS_MIN ||
    minMs > ATTACK_HOLD_MS_MAX ||
    maxMs > ATTACK_HOLD_MS_MAX
  ) {
    return t('koreaConfig.intervalRange', {
      label: holdLabel,
      min: ATTACK_HOLD_MS_MIN,
      max: ATTACK_HOLD_MS_MAX
    })
  }
  if (minMs > maxMs) {
    return t('koreaConfig.intervalOrder', { label: holdLabel })
  }

  const walk = normalizePercent(config.walkPercent)
  const teleport = normalizePercent(config.teleportPercent)
  if (walk == null || teleport == null) {
    return t('koreaConfig.labC2MoveProbInvalid')
  }
  if (walk + teleport !== 100) {
    return t('koreaConfig.labC2MoveProbSum')
  }
  return null
}

function defaultTreasureIslandFight() {
  return {
    topMin: 10,
    topMax: 20,
    midMin: 40,
    midMax: 60,
    bottomMin: 20,
    bottomMax: 30
  }
}

function parseTreasureIslandFight(content) {
  const d = defaultTreasureIslandFight()
  if (!content || typeof content !== 'object') return d
  return {
    topMin: normalizeSec(content['最上层最少打怪秒数']) ?? d.topMin,
    topMax: normalizeSec(content['最上层最多打怪秒数']) ?? d.topMax,
    midMin: normalizeSec(content['中层最少打怪秒数']) ?? d.midMin,
    midMax: normalizeSec(content['中层最多打怪秒数']) ?? d.midMax,
    bottomMin: normalizeSec(content['下层最少打怪秒数']) ?? d.bottomMin,
    bottomMax: normalizeSec(content['下层最多打怪秒数']) ?? d.bottomMax
  }
}

function serializeTreasureIslandFight(fight) {
  const d = defaultTreasureIslandFight()
  const f = fight || d
  return {
    最上层最少打怪秒数: normalizeSec(f.topMin) ?? d.topMin,
    最上层最多打怪秒数: normalizeSec(f.topMax) ?? d.topMax,
    中层最少打怪秒数: normalizeSec(f.midMin) ?? d.midMin,
    中层最多打怪秒数: normalizeSec(f.midMax) ?? d.midMax,
    下层最少打怪秒数: normalizeSec(f.bottomMin) ?? d.bottomMin,
    下层最多打怪秒数: normalizeSec(f.bottomMax) ?? d.bottomMax
  }
}

function validateTreasureIslandFight(fight, t) {
  const layers = [
    { labelKey: 'koreaConfig.layerTop', minKey: 'topMin', maxKey: 'topMax' },
    { labelKey: 'koreaConfig.layerMid', minKey: 'midMin', maxKey: 'midMax' },
    { labelKey: 'koreaConfig.layerBottom', minKey: 'bottomMin', maxKey: 'bottomMax' }
  ]
  for (const layer of layers) {
    const label = t(layer.labelKey)
    const minSec = normalizeSec(fight[layer.minKey])
    const maxSec = normalizeSec(fight[layer.maxKey])
    if (minSec == null || maxSec == null) {
      return t('koreaConfig.intervalRequired', { label })
    }
    if (
      minSec < FIGHT_SEC_MIN ||
      maxSec < FIGHT_SEC_MIN ||
      minSec > FIGHT_SEC_MAX ||
      maxSec > FIGHT_SEC_MAX
    ) {
      return t('koreaConfig.intervalRange', {
        label,
        min: FIGHT_SEC_MIN,
        max: FIGHT_SEC_MAX
      })
    }
    if (minSec > maxSec) {
      return t('koreaConfig.intervalOrder', { label })
    }
  }
  return null
}

function hangMapSearchMatch(queryRaw, optionText) {
  const raw = String(queryRaw ?? '').trim().toLowerCase()
  if (!raw) return true
  const text = String(optionText ?? '').toLowerCase()
  if (text.includes(raw)) return true
  const tokens = raw.split(/\s+/).filter(Boolean)
  return tokens.every((tok) => {
    if (!tok) return true
    if (text.includes(tok)) return true
    let ti = 0
    for (let i = 0; i < text.length && ti < tok.length; i++) {
      if (text[i] === tok[ti]) ti++
    }
    return ti === tok.length
  })
}

export default {
  name: 'CreateConfigAdventureIslandKorea',
  props: {
    id: {
      type: [String, Number],
      default: null
    },
    viewMode: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const router = useRouter()
    const saving = ref(false)
    const loading = ref(false)

    const isEditMode = computed(() => !!props.id)
    const isViewMode = computed(() => props.viewMode === true)
    const isDefaultConfig = ref(false)

    const configForm = ref({
      name: isEditMode.value ? '' : '配置001',
      description: ''
    })

    const selectedHangMap = ref('')
    const hangMapOptions = ref([])
    const hangMapFilterQuery = ref('')
    const autoKeySlots = ref(defaultAutoKeySlots(t))
    const treasureIslandFight = ref(defaultTreasureIslandFight())
    const wildBearConfig = ref(defaultWildBearConfig())
    const labC2Config = ref(defaultLabC2Config())

    const onLabC2WalkPercentChange = () => {
      syncLabC2MoveProbFromWalk(labC2Config.value)
    }

    const onLabC2TeleportPercentChange = () => {
      syncLabC2MoveProbFromTeleport(labC2Config.value)
    }

    const isTreasureIslandMap = computed(() => isTreasureIslandHangMap(selectedHangMap.value))
    const isWildBearMap = computed(() => isWildBearHangMap(selectedHangMap.value))
    const isLabC2Map = computed(() => isLabC2HangMap(selectedHangMap.value))

    const wildBearPatrolRows = computed(() => [
      {
        id: 'mid',
        label: t('koreaConfig.wildBearMidPatrol'),
        minKey: 'midPatrolMin',
        maxKey: 'midPatrolMax',
        rangeMin: MID_PATROL_MIN,
        rangeMax: MID_PATROL_MAX
      },
      {
        id: 'bottom',
        label: t('koreaConfig.wildBearBottomPatrol'),
        minKey: 'bottomPatrolMin',
        maxKey: 'bottomPatrolMax',
        rangeMin: BOTTOM_PATROL_MIN,
        rangeMax: BOTTOM_PATROL_MAX
      }
    ])

    const labC2DurationRows = computed(() => [
      {
        id: 'patrol',
        label: t('koreaConfig.labC2PatrolDuration'),
        minKey: 'patrolMinSec',
        maxKey: 'patrolMaxSec'
      },
      {
        id: 'stand',
        label: t('koreaConfig.labC2StandDuration'),
        minKey: 'standMinSec',
        maxKey: 'standMaxSec'
      }
    ])

    const treasureIslandLayers = computed(() => [
      {
        id: 'top',
        label: t('koreaConfig.layerTop'),
        minKey: 'topMin',
        maxKey: 'topMax'
      },
      {
        id: 'mid',
        label: t('koreaConfig.layerMid'),
        minKey: 'midMin',
        maxKey: 'midMax'
      },
      {
        id: 'bottom',
        label: t('koreaConfig.layerBottom'),
        minKey: 'bottomMin',
        maxKey: 'bottomMax'
      }
    ])

    const centralCommEnabled = ref(true)
    const autoClearBag = ref(false)
    const autoClearBagInterval = ref(defaultAutoClearBagInterval())
    const autoHeal = ref(true)
    const autoBuyMedicine = ref(defaultAutoBuyMedicine())
    const teleportAttackInterval = ref(defaultTeleportAttackInterval())
    const scheduledLogout = ref(defaultScheduledLogout())
    const lieDetectServer = ref('')

    const fieldsDisabled = computed(
      () => isViewMode.value || (isEditMode.value && isDefaultConfig.value)
    )

    const customKeyCount = computed(
      () => autoKeySlots.value.filter((s) => !s.locked).length
    )

    const canAddCustomKey = computed(
      () =>
        autoKeySlots.value.length < MAX_AUTO_KEYS &&
        customKeyCount.value < MAX_CUSTOM_KEYS
    )

    const addAutoKeySlot = () => {
      if (!canAddCustomKey.value) return
      autoKeySlots.value.push(
        defaultAutoKeySlot({ enabled: true, mode: MODE_INTERVAL })
      )
    }

    const removeAutoKeySlot = (index) => {
      const slot = autoKeySlots.value[index]
      if (!slot || slot.locked) return
      autoKeySlots.value.splice(index, 1)
    }

    const displayHangMapRows = computed(() => {
      const list = hangMapOptions.value.map((r) => ({ ...r }))
      const sel = selectedHangMap.value
      if (sel && !list.some((r) => r.optionValue === sel)) {
        list.unshift({ id: null, optionValue: sel })
      }
      return list
    })

    const setHangMapFilterQuery = (val) => {
      hangMapFilterQuery.value = typeof val === 'string' ? val : ''
    }

    const filteredHangMapRowsForSelect = computed(() => {
      const rows = displayHangMapRows.value
      const sel = selectedHangMap.value
      const q = hangMapFilterQuery.value
      if (!String(q).trim()) return rows
      let list = rows.filter((r) => hangMapSearchMatch(q, r.optionValue))
      const cur = sel ? rows.find((r) => r.optionValue === sel) : null
      if (cur && !list.some((r) => r.optionValue === sel)) {
        list = [cur, ...list]
      }
      return list
    })

    const onHangMapDropdownVisible = (visible) => {
      if (!visible) {
        hangMapFilterQuery.value = ''
      }
    }

    const loadHangMapOptions = async () => {
      try {
        const res = await getAdventureHangMapOptions(GAME_NAME)
        if (res.code === 200 && Array.isArray(res.data)) {
          hangMapOptions.value = res.data
        }
      } catch (error) {
        console.error('加载挂机地图选项失败:', error)
        ElMessage.warning(t('koreaConfig.hangMapLoadFail'))
      }
    }

    const loadConfigIfNeeded = async () => {
      if (!props.id) return

      loading.value = true
      try {
        const response = await getConfig(props.id)
        const config = response.data

        if (!config) {
          ElMessage.error(t('koreaConfig.notFound'))
          return
        }

        if (config.game && config.game !== GAME_NAME) {
          ElMessage.warning(t('koreaConfig.gameMismatch', { game: config.game }))
        }

        configForm.value.name = config.name || ''
        configForm.value.description = config.description || ''
        isDefaultConfig.value = !!(config.description && config.description.includes('系统为新用户自动创建的默认游戏配置'))

        const content = config.content || {}
        if (typeof content === 'object') {
          if (content['挂机地图']) {
            selectedHangMap.value = content['挂机地图']
          }
          centralCommEnabled.value =
            content['中控通讯'] === undefined ? true : parseEnabled(content['中控通讯'])
          autoClearBag.value = parseEnabled(content['自动清包'])
          autoClearBagInterval.value = parseAutoClearBagInterval(content)
          autoHeal.value =
            content['自动回血'] === undefined ? true : parseEnabled(content['自动回血'])
          autoBuyMedicine.value = parseAutoBuyMedicine(content)
          teleportAttackInterval.value = parseTeleportAttackInterval(content)
          scheduledLogout.value = parseScheduledLogout(content)
          lieDetectServer.value =
            content['测谎服务器'] != null ? String(content['测谎服务器']).trim() : ''
          autoKeySlots.value = parseAutoKeySlots(content['自动按键'], t)
          treasureIslandFight.value = parseTreasureIslandFight(content)
          wildBearConfig.value = parseWildBearConfig(content)
          labC2Config.value = parseLabC2Config(content)
        }
      } catch (error) {
        console.error('加载配置失败:', error)
        ElMessage.error(
          t('koreaConfig.loadFail', {
            msg: error.response?.data?.message || error.message || '未知错误'
          })
        )
      } finally {
        loading.value = false
      }
    }

    const saveConfig = async () => {
      if (!configForm.value.name.trim()) {
        ElMessage.error(t('koreaConfig.needConfigName'))
        return
      }
      if (!selectedHangMap.value || !String(selectedHangMap.value).trim()) {
        ElMessage.error(t('koreaConfig.needHangMap'))
        return
      }
      if (isViewMode.value) return
      if (isEditMode.value && isDefaultConfig.value) return

      if (autoBuyMedicine.value.enabled) {
        const hasMedicine = KOREA_MEDICINE_OPTIONS.some((med) => autoBuyMedicine.value[med.key])
        if (!hasMedicine) {
          ElMessage.error(t('koreaConfig.needMedicineType'))
          return
        }
      }

      if (autoClearBag.value) {
        const clearBagErr = validateAutoClearBagInterval(autoClearBagInterval.value, t)
        if (clearBagErr) {
          ElMessage.error(clearBagErr)
          return
        }
      }

      const teleportAttackErr = validateTeleportAttackInterval(teleportAttackInterval.value, t)
      if (teleportAttackErr) {
        ElMessage.error(teleportAttackErr)
        return
      }

      if (scheduledLogout.value.enabled) {
        const scheduledLogoutErr = validateScheduledLogout(scheduledLogout.value, t)
        if (scheduledLogoutErr) {
          ElMessage.error(scheduledLogoutErr)
          return
        }
      }

      if (isTreasureIslandMap.value) {
        const fightErr = validateTreasureIslandFight(treasureIslandFight.value, t)
        if (fightErr) {
          ElMessage.error(fightErr)
          return
        }
      }

      if (isWildBearMap.value) {
        const wildBearErr = validateWildBearConfig(wildBearConfig.value, t)
        if (wildBearErr) {
          ElMessage.error(wildBearErr)
          return
        }
      }

      if (isLabC2Map.value) {
        const labC2Err = validateLabC2Config(labC2Config.value, t)
        if (labC2Err) {
          ElMessage.error(labC2Err)
          return
        }
      }

      for (let i = 0; i < autoKeySlots.value.length; i++) {
        const slot = autoKeySlots.value[i]
        if (!slot.enabled) continue
        const label = slot.name?.trim() || `按键 ${i + 1}`
        const key = resolveSlotKey(slot)
        if (!key) {
          ElMessage.error(t('koreaConfig.keyRequired', { label }))
          return
        }
        if (slot.mode !== MODE_INTERVAL) continue
        const minSec = normalizeSec(slot.minSec)
        const maxSec = normalizeSec(slot.maxSec)
        if (minSec == null || maxSec == null) {
          ElMessage.error(t('koreaConfig.intervalRequired', { label }))
          return
        }
        if (minSec < SEC_MIN || maxSec < SEC_MIN || minSec > SEC_MAX || maxSec > SEC_MAX) {
          ElMessage.error(t('koreaConfig.intervalRange', { label, min: SEC_MIN, max: SEC_MAX }))
          return
        }
        if (minSec > maxSec) {
          ElMessage.error(t('koreaConfig.intervalOrder', { label }))
          return
        }
      }

      saving.value = true
      try {
        const content = {
          挂机地图: selectedHangMap.value,
          中控通讯: !!centralCommEnabled.value,
          自动清包: !!autoClearBag.value,
          ...serializeAutoClearBagInterval(autoClearBagInterval.value),
          自动回血: !!autoHeal.value,
          自动买药: serializeAutoBuyMedicine(autoBuyMedicine.value),
          ...serializeTeleportAttackInterval(teleportAttackInterval.value),
          ...serializeScheduledLogout(scheduledLogout.value),
          测谎服务器: lieDetectServer.value.trim(),
          自动按键: serializeAutoKeySlots(autoKeySlots.value)
        }
        if (isTreasureIslandMap.value) {
          Object.assign(content, serializeTreasureIslandFight(treasureIslandFight.value))
        }
        if (isWildBearMap.value) {
          Object.assign(content, serializeWildBearConfig(wildBearConfig.value))
        }
        if (isLabC2Map.value) {
          Object.assign(content, serializeLabC2Config(labC2Config.value))
        }

        const configData = {
          name: configForm.value.name,
          description: configForm.value.description,
          game: GAME_NAME,
          content
        }

        const bindWindowHint = t('koreaConfig.bindWindowHint')
        if (isEditMode.value) {
          await updateConfig(props.id, configData)
          ElMessage.success({
            message: t('koreaConfig.updateOk', { hint: bindWindowHint }),
            duration: 7000,
            showClose: true
          })
          await loadConfigIfNeeded()
        } else {
          await createConfig(configData)
          ElMessage.success({
            message: t('koreaConfig.createOk', { hint: bindWindowHint }),
            duration: 7000,
            showClose: true
          })
        }
        router.push({ name: 'ScriptConfig' })
      } catch (error) {
        console.error('保存配置失败:', error)
        ElMessage.error('保存失败: ' + (error.response?.data?.message || error.message || '未知错误'))
      } finally {
        saving.value = false
      }
    }

    onMounted(async () => {
      if (props.id) {
        await loadConfigIfNeeded()
      }
      await loadHangMapOptions()
      if (!props.id && !selectedHangMap.value && hangMapOptions.value.length > 0) {
        selectedHangMap.value = hangMapOptions.value[0].optionValue
      }
    })

    return {
      t,
      loading,
      saving,
      configForm,
      selectedHangMap,
      isTreasureIslandMap,
      isWildBearMap,
      isLabC2Map,
      treasureIslandFight,
      wildBearConfig,
      labC2Config,
      onLabC2WalkPercentChange,
      onLabC2TeleportPercentChange,
      wildBearPatrolRows,
      labC2DurationRows,
      LAB_C2_HANG_MODE_OPTIONS,
      LAB_C2_HANG_MODE_DEFAULT,
      LAB_C2_DURATION_SEC_MIN,
      LAB_C2_DURATION_SEC_MAX,
      ATTACK_HOLD_MS_MIN,
      ATTACK_HOLD_MS_MAX,
      treasureIslandLayers,
      FIGHT_SEC_MIN,
      FIGHT_SEC_MAX,
      filteredHangMapRowsForSelect,
      setHangMapFilterQuery,
      onHangMapDropdownVisible,
      autoKeySlots,
      centralCommEnabled,
      autoClearBag,
      autoClearBagInterval,
      CLEAR_BAG_INTERVAL_MIN,
      CLEAR_BAG_INTERVAL_MAX,
      autoHeal,
      autoBuyMedicine,
      teleportAttackInterval,
      TELEPORT_ATTACK_MS_MIN,
      TELEPORT_ATTACK_MS_MAX,
      scheduledLogout,
      SCHEDULED_LOGOUT_POSITION_OPTIONS,
      SCHEDULED_LOGOUT_INTERVAL_HOUR_MIN,
      SCHEDULED_LOGOUT_INTERVAL_HOUR_MAX,
      SCHEDULED_LOGOUT_DURATION_MIN,
      SCHEDULED_LOGOUT_DURATION_MAX,
      KOREA_MEDICINE_OPTIONS,
      lieDetectServer,
      KEY_SELECT_OPTIONS,
      isKeyLocked,
      MAX_CUSTOM_KEYS,
      SEC_MIN,
      SEC_MAX,
      fieldsDisabled,
      canAddCustomKey,
      customKeyCount,
      addAutoKeySlot,
      removeAutoKeySlot,
      isEditMode,
      isViewMode,
      isDefaultConfig,
      saveConfig
    }
  }
}
</script>

<style scoped>
.config-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 12px 16px 18px;
  box-sizing: border-box;
}

.page-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
}

.page-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
}

.page-subtitle {
  margin: 4px 0 0;
  color: #6b7280;
  font-size: 12px;
}

.config-card {
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  padding: 12px 14px 14px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.config-form {
  padding: 2px 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.form-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.form-item--one-line {
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
}

.form-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}

.form-label--inline {
  margin: 0;
  flex-shrink: 0;
}

.section-title {
  margin: 4px 0 0;
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  letter-spacing: 0.02em;
  padding-top: 8px;
  border-top: 1px solid #eef2f7;
}

.section-hint {
  margin: 0;
  font-size: 11px;
  color: #6b7280;
  line-height: 1.45;
}

.field-hint {
  margin: 6px 0 0;
  font-size: 11px;
  color: #6b7280;
  line-height: 1.45;
}

.form-item--lie-server {
  margin-bottom: 12px;
}

.form-item--teleport-attack-ms {
  margin-bottom: 8px;
}

.form-item--clear-bag-interval {
  margin-bottom: 8px;
}

.form-item--scheduled-logout {
  margin-bottom: 8px;
}

.scheduled-logout-enable {
  margin-bottom: 8px;
}

.scheduled-logout-fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-left: 0;
}

.scheduled-logout-row {
  display: flex;
  align-items: center;
  gap: 4px 8px;
  flex-wrap: wrap;
}

.scheduled-logout-inline-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
}

.scheduled-logout-row .scheduled-logout-inline-label:not(:first-child) {
  margin-left: 8px;
}

.scheduled-logout-position {
  margin-right: 4px;
}

.native-radio {
  width: 14px;
  height: 14px;
  margin: 0;
  accent-color: #409eff;
}

.lab-c2-hang-mode-row {
  margin-bottom: 8px;
}

.lab-c2-hang-mode-options {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
}

.lab-c2-hang-mode-option {
  margin-right: 0;
}

.lab-c2-auto-return {
  display: inline-flex;
  margin: 0 0 10px;
}

.lab-c2-move-prob-row {
  margin-top: 4px;
}

.lab-c2-move-prob-fields {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 20px;
}

.lab-c2-move-prob-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.lab-c2-move-prob-label {
  font-size: 12px;
  color: #374151;
  white-space: nowrap;
}

.lab-c2-move-prob-hint {
  margin-top: 4px;
}

.form-item--attack-hold-ms {
  margin-bottom: 8px;
}

.check-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px 24px;
  margin-bottom: 4px;
}

.check-row--auto-medicine {
  margin-top: -2px;
  padding-left: 0;
}

.auto-key-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.auto-key-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e8edf3;
}

.auto-key-row--locked {
  background: #f1f5f9;
}

.auto-key-row-fields {
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 10px;
  min-width: 0;
}

.auto-key-delete-btn {
  flex-shrink: 0;
  align-self: center;
  white-space: nowrap;
  padding: 6px 8px;
}

.auto-key-enable {
  flex-shrink: 0;
}

.auto-key-name {
  width: 7.5rem;
  flex: 0 1 7.5rem;
  min-width: 5.5rem;
}

.auto-key-letter {
  width: 5.25rem;
  flex: 0 0 auto;
}

.auto-key-range {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 0 1 auto;
}

.auto-key-sec {
  width: 5.5rem;
  flex: 0 0 auto;
  text-align: center;
}

.auto-key-range-sep {
  color: #94a3b8;
  font-size: 12px;
}

.auto-key-range-unit {
  font-size: 12px;
  color: #64748b;
}

.auto-key-instant-tag {
  font-size: 12px;
  color: #64748b;
  padding: 4px 8px;
  border-radius: 6px;
  background: #eef2ff;
  border: 1px solid #e0e7ff;
  white-space: nowrap;
}

.treasure-layer-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e8edf3;
}

.treasure-layer-label {
  flex: 0 0 calc(3.5rem + 50px);
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}

.treasure-layer-range {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 0 1 auto;
}

.form-check {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  user-select: none;
}

.native-checkbox {
  width: 16px;
  height: 16px;
  accent-color: #2563eb;
}

.native-select {
  height: 32px;
  border: 1px solid #d6dbe6;
  border-radius: 8px;
  background: #fff;
  color: #1f2937;
  font-size: 13px;
  padding: 0 8px;
  outline: none;
}

.native-select:disabled,
.native-input:disabled {
  background: #f5f7fa;
  color: #9aa2af;
  cursor: not-allowed;
}

.text-btn {
  border: none;
  background: transparent;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 0;
}

.text-btn--add {
  color: #2563eb;
  align-self: flex-start;
}

.text-btn--danger {
  color: #dc2626;
  flex-shrink: 0;
}

.text-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.form-item--hang-map .hang-map-field-body {
  flex: 1;
  min-width: 12rem;
  max-width: min(52rem, 100%);
}

.hang-map-el-select {
  width: 100%;
  --el-select-multiple-input-color: #111827;
}

.hang-map-el-select :deep(.el-select__wrapper) {
  min-height: 36px;
  border-radius: 8px;
  box-shadow: 0 0 0 1px #d6dbe6 inset;
}

.hang-map-el-select :deep(.el-select__placeholder.is-transparent) {
  color: #111827;
  opacity: 1;
}

.native-input {
  width: 100%;
  height: 32px;
  border: 1px solid #d6dbe6;
  border-radius: 8px;
  background: #fff;
  color: #1f2937;
  font-size: 13px;
  padding: 0 10px;
  outline: none;
}

.native-input.treasure-sec-input {
  width: 50px;
  min-width: 50px;
  max-width: 50px;
  flex: 0 0 50px;
  padding: 0 4px;
  text-align: center;
}

.teleport-attack-range {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
  align-self: flex-start;
}

.clear-bag-interval-range {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
  align-self: flex-start;
}

.attack-hold-range {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
  align-self: flex-start;
}

.native-input:focus {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.16);
}

.native-input:disabled {
  background: #f5f7fa;
  color: #9aa2af;
  cursor: not-allowed;
}

.hang-map-dropdown-empty {
  padding: 12px 14px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
  text-align: center;
}

.bottom-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}

.primary-btn {
  height: 32px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  background: #2563eb;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.primary-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.view-mode-hint {
  margin-top: 8px;
  color: #909399;
  font-size: 11px;
}

.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top: 3px solid #007aff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@media (max-width: 560px) {
  .form-item--hang-map {
    flex-direction: column;
    align-items: stretch;
  }

  .form-item--hang-map .hang-map-field-body {
    width: 100%;
    max-width: none;
  }

  .auto-key-row {
    flex-direction: column;
    align-items: stretch;
  }

  .auto-key-row-fields {
    flex-direction: column;
    align-items: stretch;
  }

  .auto-key-delete-btn {
    align-self: flex-end;
  }

  .auto-key-name,
  .auto-key-letter,
  .auto-key-sec {
    width: 100%;
    max-width: none;
  }
}
</style>

<style>
.hang-map-select-popper.el-select__popper {
  border-radius: 10px;
}

.hang-map-select-popper .el-select-dropdown__wrap {
  max-height: min(320px, 46vh);
}

.hang-map-select-popper .el-select-dropdown__item {
  padding: 8px 12px;
  font-size: 13px;
  line-height: 1.45;
  white-space: normal;
  word-break: break-word;
  min-height: auto;
  height: auto;
}
</style>
